import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, Tabs, TabsContent, TabsList, TabsTrigger } from "@sagot/ui";
import { useNavigate, useParams } from "react-router";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { formatNumber } from "../shared/format";
import { AccessMatrixPanel } from "./access-matrix-panel";
import { AudiencesPanel } from "./audiences-panel";
import { EndUsersPanel } from "./end-users-panel";
import { IdentityProvidersPanel } from "./identity-providers-panel";
import { useAudienceAgents, useEndUsers } from "./use-audiences-data";

export const AUDIENCE_TABS = ["aud", "matrix", "users", "idp"] as const;
export type AudienceTab = (typeof AUDIENCE_TABS)[number];
const DEFAULT_TAB: AudienceTab = "aud";

function isTab(value: string | undefined): value is AudienceTab {
  return AUDIENCE_TABS.some((tab) => tab === value);
}

function Header() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agents = useAudienceAgents();
  const firstAgent = agents.data?.[0];
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("deployKnowledge.audiences.title")}</h1>
        <p className="mt-1 max-w-2xl text-fg-muted">
          {t("deployKnowledge.audiences.subtitleLead")} <strong className="text-fg">{t("deployKnowledge.audiences.subtitleBefore")}</strong>{" "}
          {t("deployKnowledge.audiences.subtitleTail")}
        </p>
      </div>
      {firstAgent ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="secondary">
            <AppLink href={consoleRoutes.playground(summary.slug, firstAgent.id)}>
              <Icon name="eye" />
              {t("deployKnowledge.audiences.testAs")}
            </AppLink>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function FilteringExplainer() {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 rounded-lg border border-line bg-primary-soft px-4 py-3">
      <Icon name="shield-check" className="mt-0.5 size-5 shrink-0 text-primary-ink" />
      <div className="text-sm">
        <p className="font-bold">{t("deployKnowledge.audiences.explainerTitle")}</p>
        <p className="mt-0.5 text-fg-muted">{t("deployKnowledge.audiences.explainerBody")}</p>
      </div>
    </div>
  );
}

/** `/w/:ws/audiences/:tab?` — audiences, access matrix, end-user directory, identity providers. */
export function AudiencesPage() {
  const { t, i18n } = useTranslation();
  const { summary } = useWorkspaceContext();
  const navigate = useNavigate();
  const param = useParams().tab;
  const tab: AudienceTab = isTab(param) ? param : DEFAULT_TAB;
  const totals = useEndUsers({ q: "", audience: null, page: 1 });
  const go = (next: string) => {
    void navigate(consoleRoutes.audiences(summary.slug, next === DEFAULT_TAB ? undefined : next));
  };
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <Header />
      <FilteringExplainer />
      <Tabs value={tab} onValueChange={go}>
        <TabsList aria-label={t("deployKnowledge.audiences.tabsLabel")} className="overflow-x-auto">
          <TabsTrigger value="aud">{t("deployKnowledge.audiences.tabAudiences")}</TabsTrigger>
          <TabsTrigger value="matrix">{t("deployKnowledge.audiences.tabMatrix")}</TabsTrigger>
          <TabsTrigger value="users">
            {t("deployKnowledge.audiences.tabUsers")}
            {totals.data ? <Badge className="ml-1">{formatNumber(totals.data.total, i18n.language)}</Badge> : null}
          </TabsTrigger>
          <TabsTrigger value="idp">{t("deployKnowledge.audiences.tabIdp")}</TabsTrigger>
        </TabsList>
        <TabsContent value="aud" className="mt-6">
          <AudiencesPanel onEditAccess={() => { go("matrix"); }} />
        </TabsContent>
        <TabsContent value="matrix" className="mt-6">
          <AccessMatrixPanel />
        </TabsContent>
        <TabsContent value="users" className="mt-6">
          <EndUsersPanel />
        </TabsContent>
        <TabsContent value="idp" className="mt-6">
          <IdentityProvidersPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
